import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { useLang } from "@/lib/i18n";
import ContactButtons from "@/components/ContactButtons";

const OFFICIAL_EMAIL = "alisalemps.com@gmail.com";
const EMAIL_RE = /^[\w.+-]+@[\w-]+\.[\w.-]+$/;

export default function ContactPage() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [body, setBody] = useState("");
    const [website, setWebsite] = useState("");
    const [sending, setSending] = useState(false);
    const [err, setErr] = useState({});

    const { t, p } = useLang();
    useEffect(() => { document.title = `${t("contact")} | ${t("siteName")}`; }, [t]);

    const submit = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!name.trim()) errs.name = t("errName");
        if (!email.trim()) errs.email = t("errEmail");
        else if (!EMAIL_RE.test(email.trim())) errs.email = t("errEmailBad");
        if (!body.trim()) errs.body = t("errBody");
        setErr(errs);
        if (Object.keys(errs).length) return;
        setSending(true);
        try {
            await api.post("/public/contact", { name, email, body, website });
            toast.success(t("contactSent"));
            setName(""); setEmail(""); setBody("");
        } catch (e2) {
            toast.error(formatError(e2.response?.data?.detail) || t("contactFail"));
        } finally { setSending(false); }
    };

    const field = "w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-brand-red focus:outline-none py-2 text-brand-black placeholder:text-gray-400 dark:text-white dark:border-gray-600 dark:focus:border-brand-red";

    return (
        <div className="mx-auto max-w-2xl px-4 py-12">
            <nav className="text-xs text-gray-500 mb-6" aria-label="breadcrumb" data-testid="contact-breadcrumb">
                <Link to={p || "/"} className="hover:text-brand-red">{t("home")}</Link>
                <span className="mx-1">/</span>
                <span className="text-brand-black dark:text-white font-bold">{t("contact")}</span>
            </nav>
            <h1 className="font-display font-black text-3xl md:text-4xl text-brand-black dark:text-white mb-2">{t("contact")}</h1>
            <p className="text-base text-gray-700 dark:text-gray-200 leading-relaxed mb-6" data-testid="contact-intro">{t("contactIntro")}</p>
            <div className="mb-6"><ContactButtons /></div>
            <div className="mb-10 flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-brand-red" />
                <a href={`mailto:${OFFICIAL_EMAIL}`} className="text-brand-red font-bold underline break-all" dir="ltr" data-testid="contact-email-link">{OFFICIAL_EMAIL}</a>
            </div>
            <form onSubmit={submit} noValidate className="space-y-6" data-testid="contact-form">
                {/* Honeypot: invisible to sighted users, keyboard and screen readers. */}
                <div className="hidden" aria-hidden="true">
                    <input type="text" name="website" id="contact-website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" />
                </div>
                <div>
                    <label htmlFor="contact-name" className="block text-xs font-bold text-gray-500 mb-1">{t("name")}</label>
                    <input id="contact-name" name="name" type="text" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className={field}
                        aria-invalid={!!err.name} aria-describedby={err.name ? "err-name" : undefined} data-testid="contact-name" />
                    {err.name && <p id="err-name" className="text-xs text-brand-red mt-1" role="alert" data-testid="err-name">{err.name}</p>}
                </div>
                <div>
                    <label htmlFor="contact-email" className="block text-xs font-bold text-gray-500 mb-1">{t("email")}</label>
                    <input id="contact-email" name="email" type="email" required autoComplete="email" inputMode="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} className={field}
                        aria-invalid={!!err.email} aria-describedby={err.email ? "err-email" : undefined} data-testid="contact-email" />
                    {err.email && <p id="err-email" className="text-xs text-brand-red mt-1" role="alert" data-testid="err-email">{err.email}</p>}
                </div>
                <div>
                    <label htmlFor="contact-body" className="block text-xs font-bold text-gray-500 mb-1">{t("message")}</label>
                    <textarea id="contact-body" name="body" required value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={5000} className={`${field} resize-y`}
                        aria-invalid={!!err.body} aria-describedby={err.body ? "err-body" : undefined} data-testid="contact-body" />
                    {err.body && <p id="err-body" className="text-xs text-brand-red mt-1" role="alert" data-testid="err-body">{err.body}</p>}
                </div>
                <div>
                    <Button type="submit" disabled={sending} className="bg-brand-red hover:bg-brand-red-dark rounded-sm px-8" data-testid="contact-submit">
                        <Send className="w-4 h-4" /> {sending ? t("sending") : t("sendMessage")}
                    </Button>
                </div>
            </form>
        </div>
    );
}
